import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableFooter,
    LinearProgress,
    Pagination,
    IconButton,
    Icon,
    Typography,
} from '@mui/material';

import { useDebounce } from '../../shared/hooks';
import { ExportButtons, Toolbar } from '../../shared/components';
import { Environment } from '../../shared/environments';
import { IEstablishment } from '../../shared/protocolos';
import { LayoutBaseDePagina } from '../../shared/layouts';
import { formatCNPJ, formatCPF, formatPhoneNumber, formatCEP } from 'shared/utils';
import { EstablishmentService } from '../../shared/services/establishment/establishmentService';

export const Establishment: React.FC = () => {
    const navigate = useNavigate();
    const { debounce } = useDebounce();
    const userId = localStorage.getItem('LOGGED_USER_ID');
    const [searchParams, setSearchParams] = useSearchParams();

    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState<IEstablishment[]>([]);

    const busca = useMemo(() => {
        return searchParams.get('busca') || '';
    }, [searchParams]);

    const pagina = useMemo(() => {
        return Number(searchParams.get('pagina') || '1');
    }, [searchParams]);

    useEffect(() => {
        setIsLoading(true);

        debounce(async () => {
            handleTableList();
        });
    }, [busca, pagina]);

    const handleTableList = async () => {
        EstablishmentService.listAll('id', pagina, 'asc', busca).then((result) => {
            setIsLoading(false);

            if (result instanceof Error) {
                alert(result.message);
                return;
            }

            setTotalCount(result.totalCount);
            setRows(result.data);
        });
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Realmente deseja apagar?')) return;

        setIsLoading(true);

        EstablishmentService.deleteById(id, userId as string).then((result) => {
            setIsLoading(false);

            if (result instanceof Error) {
                alert(result.message);
                return;
            }

            handleTableList();
            alert('Registro apagado com sucesso!');
        });
    };

    return (
        <LayoutBaseDePagina
            barraDeFerramentas={
                <Toolbar
                    mostrarInputBusca
                    textoDaBusca={busca}
                    textoBotaoNovo="Nova"
                    acoesExtras={<ExportButtons title="Estabelecimentos" fileName="estabelecimentos" rows={rows} totalCount={totalCount} pageSize={Environment.LIMIT}
                        loadPage={async exportPage => { const result = await EstablishmentService.listAll('id', exportPage, 'asc', busca); return result instanceof Error ? result : result.data; }} disabled={isLoading} columns={[
                        { label: 'Nome', value: row => row.name }, { label: 'CNPJ', value: row => formatCNPJ(row.cnpj) },
                        { label: 'Telefone', value: row => formatPhoneNumber(row.telefone) }, { label: 'Nome Fantasia', value: row => row.nomeFantasia },
                        { label: 'Razão Social', value: row => row.razaoSocial }, { label: 'Endereço', value: row => row.endereco },
                        { label: 'Bairro', value: row => row.bairro }, { label: 'CEP', value: row => formatCEP(row.cep) },
                        { label: 'Cidade', value: row => row.cidade }, { label: 'UF', value: row => row.uf },
                        { label: 'Capa', value: row => row.photograph }, { label: 'Administrador', value: row => row.user?.name },
                        { label: 'Email', value: row => row.user?.email }, { label: 'CPF', value: row => row.user?.cpf ? formatCPF(row.user.cpf) : '' },
                        { label: 'URL', value: row => row.url },
                    ]} />}
                    aoClicarEmNovo={() => navigate('/establishment/detalhe/nova')}
                    aoMudarTextoDeBusca={(texto) =>
                        setSearchParams({ busca: texto, pagina: '1' }, { replace: true })
                    }
                />
            }
        >
            <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width={100}>Ações</TableCell>
                            <TableCell>Nome</TableCell>
                            <TableCell>CNPJ</TableCell>
                            <TableCell>Telefone</TableCell>
                            <TableCell>Nome Fantasia</TableCell>
                            <TableCell>Razão Social</TableCell>
                            <TableCell>Endereço</TableCell>
                            <TableCell>Bairro</TableCell>
                            <TableCell>CEP</TableCell>
                            <TableCell>Cidade</TableCell>
                            <TableCell>UF</TableCell>
                            <TableCell>Capa</TableCell>
                            <TableCell>Administrador</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>CPF</TableCell>
                            <TableCell>URL</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <IconButton onClick={() => handleDelete(row.id as string)}>
                                        <Icon>delete</Icon>
                                    </IconButton>

                                    <IconButton onClick={() => navigate(`/establishment/detalhe/${row.id}`)}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" variant="inherit">
                                        {row.name}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {formatCNPJ(row.cnpj)}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {formatPhoneNumber(row.telefone)}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.nomeFantasia}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.razaoSocial}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.endereco}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.bairro}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {formatCEP(row.cep)}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.cidade}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.uf}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.photograph}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.user?.name}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.user?.email}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {formatCPF(row.user?.cpf as any)}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                                        {row.url}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>

                    {totalCount === 0 && !isLoading && (
                        <caption>{Environment.LISTAGEM_VAZIA}</caption>
                    )}

                    <TableFooter>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={16}>
                                    <LinearProgress variant="indeterminate" />
                                </TableCell>
                            </TableRow>
                        )}

                        {totalCount > Environment.LIMIT && (
                            <TableRow>
                                <TableCell colSpan={16}>
                                    <Pagination
                                        page={pagina}
                                        count={Math.ceil(totalCount / Environment.LIMIT)}
                                        onChange={(_, newPage) =>
                                            setSearchParams(
                                                { busca, pagina: newPage.toString() },
                                                { replace: true }
                                            )
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableFooter>
                </Table>
            </TableContainer>
        </LayoutBaseDePagina>
    );
};
